import { JNode } from "$components/JTree/JTree";
import { Order, OrderStatus } from "$types";
import OrderLabel from "./OrderLabel";
import ProductLabel from "./ProductLabel";
import { TreeProduct } from "./types";

type Props = {
  allowOrderWithoutChildrenCheck?: boolean;
  displayOrderWithoutProducts?: boolean;
  hideProducts?: boolean;
  hideProductsWithQtyZero?: boolean;
  showCheckbox?: boolean;
  onProductDelete?: (treeProduct: TreeProduct) => void;
  onProductQuantityChange?: (quantity: number, product: any) => void;
};

const useOrderForTree = ({
  allowOrderWithoutChildrenCheck,
  displayOrderWithoutProducts,
  hideProducts,
  hideProductsWithQtyZero,
  onProductDelete,
  onProductQuantityChange,
  showCheckbox,
}: Props) => {
  const suborderProductsToJNode = (
    order,
    suborder,
    isCanceled: boolean
  ): JNode[] | null => {
    return suborder.suborderProducts?.map((product) => {
      if (hideProductsWithQtyZero && product.quantity === 0) return null;
      const hasOnlyOneSuborder =
        order.hasOnlyOneSuborder ?? order.suborders.length === 1;
      const treeProduct: TreeProduct = {
        product: {
          id: product.id,
          name: product.name,
          quantity: product.quantity,
          reference: product.reference,
          unit: product.unit,
          integrableQuantity: product.integrableQuantity,
          initialQuantity:
            product.initialQuantity == null // == null works for both null and undefined
              ? product.quantity
              : product.initialQuantity,
        },
        suborder: {
          id: suborder.id,
          orderReference: order.reference,
          reference: suborder.reference,
          dockyard: suborder.dockyard,
          project: suborder.project,
        },
        order: {
          hasOnlyOneSuborder,
          id: order.id,
          reference: order.reference,
          suborders: order.suborders
            .filter((suborder) =>
              suborder.suborderProducts.some((p) => p.id === product.id)
            )
            .map((suborder) => ({
              id: suborder.id,
              orderReference: order.id,
              reference: suborder.reference,
              dockyard: suborder.dockyard,
              project: suborder.project,
              suborderProducts: suborder.suborderProducts
                .filter((p) => p.id === product.id)
                .map((product) => ({
                  id: product.id,
                  name: product.name,
                  quantity: product.quantity,
                  reference: product.reference,
                  unit: product.unit,
                })),
            })),
          dockyard: order.dockyard,
          project: order.project,
        },
      };
      return {
        disabled:
          isCanceled || (showCheckbox && product.integrableQuantity === 0),
        value: JSON.stringify({
          orderId: order.id,
          suborderId: suborder.id,
          productId: product.id,
        }),
        showCheckbox,
        label: (
          <ProductLabel
            isCheckable={showCheckbox}
            largerVariant={hasOnlyOneSuborder}
            onProductDelete={onProductDelete}
            onQuantityChange={onProductQuantityChange}
            treeProduct={treeProduct}
          />
        ),
      };
    });
  };

  const orderToJNodeExceptChildren = (
    order,
    isCanceled,
    childrenNodes: JNode[],
    parentOrderId?: number
  ): JNode => {
    const isOrder = !parentOrderId;
    const { hasOnlyOneSuborder } = order;
    return {
      disabled:
        isCanceled ||
        (childrenNodes.length > 0 &&
          childrenNodes.every((node) => !!node.disabled)),
      value: JSON.stringify({
        ...(isOrder
          ? hasOnlyOneSuborder
            ? { orderId: order.id, suborderId: order.suborders[0].id }
            : { orderId: order.id }
          : { orderId: parentOrderId, suborderId: order.id }),
      }),
      showCheckbox,
      label: <OrderLabel order={order} />,
    };
  };

  const suborderToJNode = (order, suborder, isCanceled): JNode | null => {
    const childrenNodes = hideProducts
      ? []
      : suborderProductsToJNode(order, suborder, isCanceled).filter(
          (c) => c !== null
        );
    if (childrenNodes.length === 0 && !hideProducts) return null;
    const nodeData = orderToJNodeExceptChildren(
      suborder,
      isCanceled,
      childrenNodes,
      order.id
    );
    return {
      ...nodeData,
      ...(childrenNodes.length > 0 && { children: childrenNodes }),
    };
  };

  const orderToJNode = (
    order: Order & { hasOnlyOneSuborder?: boolean }
  ): JNode | null => {
    const { hasOnlyOneSuborder, status, suborders } = order;
    const isCanceled = status === OrderStatus.CANCELED;
    let childrenNodes = [];
    if (suborders?.length > 0) {
      if (hasOnlyOneSuborder) {
        childrenNodes = hideProducts
          ? []
          : suborderProductsToJNode(order, suborders[0], isCanceled);
      } else {
        childrenNodes = suborders
          .map((suborder) => suborderToJNode(order, suborder, isCanceled))
          .filter((n) => n !== null);
      }
    }

    if (childrenNodes.length === 0 && !displayOrderWithoutProducts) return null;

    const allChildrenDisabled = childrenNodes.every((c) => c.disabled);

    return {
      ...orderToJNodeExceptChildren(order, isCanceled, childrenNodes),
      disabled:
        isCanceled || (allChildrenDisabled && !allowOrderWithoutChildrenCheck),
      children: childrenNodes,
    };
  };

  return { formatOrderForTree: orderToJNode };
};

export default useOrderForTree;
